import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronsUpDown, Plus, Settings2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ProjectAvatar, HealthDot } from '@/components/ProjectAvatar';
import { apiFetch, getProject, setProject } from '@/lib/api';
import { healthOf, isOfficial, type Project, type ProjectHealth } from '@/lib/projects';
import { NewProjectDialog } from '@/components/NewProjectDialog';
import { useT } from '@/lib/i18n';

/** Hook compartilhado: lista de projetos + saúde agregada (semáforo). */
export function useProjects() {
  return useQuery({
    queryKey: ['projects'],
    queryFn: async (): Promise<Project[]> =>
      ((await apiFetch('GET', '/projects')).data as { data?: Project[] })?.data ?? [],
  });
}

export function useProjectsHealth() {
  return useQuery({
    queryKey: ['projects', 'health'],
    queryFn: async (): Promise<Record<string, ProjectHealth>> => {
      const list =
        ((await apiFetch('GET', '/projects/health')).data as { data?: ProjectHealth[] })?.data ??
        [];
      return Object.fromEntries(list.map((h) => [h.project_id, h]));
    },
    refetchInterval: 10000,
  });
}

/** Seletor de PROJETO ativo — destacado no topo do sidebar. Troca define o X-Project-Id de tudo. */
export function ProjectSwitcher({ isAdmin }: { isAdmin: boolean }) {
  const t = useT();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: projects = [] } = useProjects();
  const { data: health = {} } = useProjectsHealth();

  const active = getProject();
  // Sem projeto ativo ainda: assume o primeiro (sem reload — primeira definição).
  useEffect(() => {
    if (!active && projects.length) setProject(projects[0].id);
  }, [active, projects]);

  const current = projects.find((p) => p.id === active) ?? projects[0];
  const [creating, setCreating] = useState(false);

  const switchTo = (id: string) => {
    if (id === active) return;
    setProject(id);
    window.location.reload(); // recarrega com o novo contexto de projeto
  };

  // Criar daqui abre o MESMO diálogo da página Projetos: a escolha do trilho é
  // irreversível, e não pode depender de por onde o cliente entrou.
  const onCreated = (p: Project) => {
    qc.invalidateQueries({ queryKey: ['projects'] });
    setProject(p.id);
    // Projeto oficial começa no passo a passo de conexão, não numa tela vazia.
    if (isOfficial(p)) navigate('/numbers');
    else window.location.reload();
  };

  if (!current) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger className="flex w-full items-center gap-2.5 rounded-lg border bg-card p-2 text-left outline-none transition-colors hover:bg-accent">
          <div className="relative">
            <ProjectAvatar project={current} size={36} />
            <HealthDot
              health={healthOf(health[current.id])}
              className="absolute -bottom-0.5 -right-0.5 ring-2 ring-card"
            />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold leading-tight">{current.name}</p>
            <p className="truncate text-xs text-muted-foreground">
              {isOfficial(current)
                ? <span className="font-medium text-[#128C7E] dark:text-[#25D366]">{t('project.officialBadge')}</span>
                : t('project.label')}
            </p>
          </div>
          <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-60">
          {projects.map((p) => (
            <DropdownMenuItem key={p.id} onClick={() => switchTo(p.id)} className="gap-2.5 py-2">
              <ProjectAvatar project={p} size={28} />
              <span className="flex-1 truncate">{p.name}</span>
              {isOfficial(p) && (
                <span className="rounded bg-[#25D366]/15 px-1.5 py-0.5 text-[10px] font-medium text-[#128C7E] dark:text-[#25D366]">
                  {t('project.officialBadge')}
                </span>
              )}
              <HealthDot health={healthOf(health[p.id])} />
              {p.id === active && <Check className="size-4 text-primary" />}
            </DropdownMenuItem>
          ))}
          {isAdmin && (
            <DropdownMenuItem
              onClick={() => navigate('/projects')}
              className="gap-2 border-t text-muted-foreground"
            >
              <Settings2 className="size-4" /> {t('project.manage')}
            </DropdownMenuItem>
          )}
          {isAdmin && (
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                setCreating(true);
              }}
              className="gap-2 text-primary"
            >
              <Plus className="size-4" /> {t('project.new')}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <NewProjectDialog open={creating} onOpenChange={setCreating} onCreated={onCreated} />
    </>
  );
}
